import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
} from '@angular/core';
import { InterchainDashboardService } from '../../services/interchain-dashboard.service';
import { DefaultLanguageState } from 'app/states/default-language/default-language.state';
import { Store } from '@ngxs/store';
import { UtilService } from 'app/shared/util.service';
import { RecommendationData } from '../../models/RecommendationData.model';
import { SmartReport } from '../../models/SmartReport.model';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { BreakpointService } from 'app/core-lib/services/breakpoints/breakpoint.service';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { PAGE_HEIGHT_MM, PAGE_WIDTH_MM, planPages } from './pdf-pagination';

const MYHOTEL_URL = 'https://www.myhotel.cl';

/** Holgura al comparar coordenadas de layout: el redondeo del navegador no debe abrir ni cerrar un corte. */
const GEOMETRY_EPSILON_PX = 0.5;
/** Un canal por debajo de esto ya no es fondo blanco: hay algo pintado en ese píxel. */
const INK_THRESHOLD = 250;
/**
 * Tinta que puede tener una fila y seguir contando como libre, en proporción al ancho.
 *
 * Existe por los bordes verticales de la tabla de recomendaciones: cruzan la tabla de arriba abajo,
 * así que sin esta tolerancia ninguna fila de la tabla estaría nunca libre y no habría dónde cortar
 * entre los renglones de una celda. Medido en el reporte a escala 2 (canvas de 1588px de ancho):
 * las filas que solo llevan los tres bordes tienen 9px de tinta y las que tienen texto arrancan en
 * 20px, así que ~12px separa los dos casos con margen. Los fondos de color no se ven afectados:
 * ocupan el ancho completo.
 */
const MAX_BLANK_ROW_INK_RATIO = 0.0075;
/** Filas del canvas que se leen de una vez al buscar franjas en blanco. */
const INK_SCAN_STRIP_ROWS = 128;

interface PdfLink {
  top: number;
  left: number;
  width: number;
  height: number;
  url: string;
}

/** Geometría del fragmento, medida en el clon que html2canvas está por pintar. */
interface FragmentMeasures {
  /** Tops de los `data-pdf-block`: los cortes de página preferidos. */
  blockTopsPx: number[];
  /** Cajas que no se pueden partir (`data-pdf-keep-next`), sin la línea que las sigue. */
  keepBoxesPx: VerticalSpan[];
  links: PdfLink[];
  /** Ancho renderizado en px CSS, fraccional: base de la conversión px CSS -> mm. */
  elementWidthPx: number;
  /** Alto renderizado en px CSS, fraccional. */
  contentHeightPx: number;
}

interface RenderedHtml extends FragmentMeasures {
  canvas: HTMLCanvasElement;
}

/** Franja vertical del fragmento en px CSS relativos a su raíz: una línea de texto o una zona indivisible. */
interface VerticalSpan {
  topPx: number;
  bottomPx: number;
}

/** Link de la cabecera o el pie, ya convertido a mm relativos a su propio fragmento. */
interface ChromeLink {
  leftMm: number;
  topMm: number;
  widthMm: number;
  heightMm: number;
  url: string;
}

interface PdfChrome {
  headerImg: string;
  headerHeightMm: number;
  headerLinks: ChromeLink[];
  footerImg: string;
  footerHeightMm: number;
  footerLinks: ChromeLink[];
}

@Component({
  selector: 'mh-recommendation-table-widget',
  templateUrl: './recommendation-table-widget.component.html',
  styleUrls: ['./recommendation-table-widget.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecommendationTableWidgetComponent implements OnChanges, OnInit {
  @Input() hotelRecommendations: RecommendationData[] = [];
  @Input() loadingRecommendations = true;

  public generatingPdf = signal(false);
  public selectedHotel = signal<RecommendationData | null>(null);
  public individualRecommendation = signal<RecommendationData | null>(null);
  public isIndividualView = signal(true);

  public pdfOptions = {
    scale: 2,
    useCORS: true,
    allowTaint: true,
  };
  public pdfStyles = `
    <style>
      body { margin: 0; padding: 0; }
      /* La tipografia se fija con .pdf-report * y no heredando desde body porque general.scss
         aplica globalmente "div, label, h2, h3, h4, h5, span { font-family: Yantramanav }", y una
         regla directa sobre div le gana a lo que el elemento heredaria del body: sin esto el
         reporte sale en Yantramanav aunque el CSS de aca pida otra cosa. */
      .pdf-report, .pdf-report * { font-family: 'Rubik', Arial, Helvetica, sans-serif; }
      .header { background-color: #0F93CC; color: white; padding: 25px 50px; display: flex; justify-content: space-between; align-items: center }
      .header h1 { margin: 0; font-size: 20px; font-weight: bold; }
      .header h2 { margin: 0; font-size: 16px; font-weight: normal; padding-bottom: 20px; }
      .header .prepared-text { margin: 0; padding: 0; font-size: 14px; font-weight: normal; line-height: 1; }
      .contenido { padding-top: 10px; padding-bottom: 50px; }
      .seccion { margin-bottom: 20px; }
      .seccion-titulo { background-color: #E7ECFF; color: #036D9A; padding: 0 50px; padding-bottom: 16px; margin-bottom: 10px; font-weight: semiboold; width: 100%; font-size: 16px; }
      .aviso { font-size: 12px; font-style: italic; margin-bottom: 20px; padding: 0 50px; text-align: justify; }
      /* Justificado solo en los parrafos del cuerpo: en las celdas de la tabla, que son angostas,
         estirar el espaciado abriria huecos entre palabras. La ultima linea queda en bandera. */
      .seccion-contenido { padding: 0 50px; text-align: justify; }
      .aviso p, .seccion-contenido p, .tabla-recomendaciones td, .tabla-recomendaciones td p { white-space: pre-line; }
      .tabla-recomendaciones { width: 100%; border-collapse: collapse; margin-top: 10px; }
      .tabla-recomendaciones th { background-color: #e8eeff; padding: 0 15px 15px 15px; text-align: left; border: 1px solid #ddd; }
      .tabla-recomendaciones td { text-align: left; border: 1px solid #ddd; padding: 0 15px 15px 15px;}
      .footer {
        background-color: #0F93CC;
        color: white;
        padding: 15px 0px;
        padding-bottom: 35px;
        text-align: center;
        width: 100%;
      }
      .footer h3 { margin: 5px 0; font-size: 16px; }
      .footer a { margin: 10px 0; font-size: 14px; }
    </style>
  `;

  public searchTerm = '';
  public filteredHotels = signal<RecommendationData[]>([]);

  private interchainDashboardService = inject(InterchainDashboardService);
  private utilsService = inject(UtilService);
  private store = inject(Store);
  private breakpointService = inject(BreakpointService);
  private translateService = inject(TranslateService);

  ngOnInit() {
    this.setRecommendationState();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hotelRecommendations) {
      this.setRecommendationState();
    }
  }

  private setRecommendationState() {
    this.isIndividualView.set(this.hotelRecommendations.length <= 1);
    this.individualRecommendation.set(this.hotelRecommendations[0] || null);
    this.filteredHotels.set(this.hotelRecommendations);
  }

  filterHotels() {
    if (!this.searchTerm) {
      this.filteredHotels.set(this.hotelRecommendations);
      return;
    }

    const searchTermLower = this.searchTerm.toLowerCase();
    this.filteredHotels.set(
      this.hotelRecommendations.filter((hotel) => hotel.customer_name.toLowerCase().includes(searchTermLower)),
    );
  }

  async handlePdfReport(hotel: RecommendationData) {
    try {
      this.selectedHotel.set(hotel);
      this.generatingPdf.set(true);
      const languageId =
        +this.utilsService.getLanguageId() || this.store.selectSnapshot(DefaultLanguageState.languageId);
      const response = await firstValueFrom(
        this.interchainDashboardService.getSmartReportData(hotel.smart_report_id, languageId),
      );
      await this.createPdfReport(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      this.generatingPdf.set(false);
    }
  }

  async createPdfReport(reportData: SmartReport): Promise<void> {
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');

      // Las webfonts tienen que estar cargadas ANTES de medir cualquier fragmento: html2canvas
      // espera `fonts.ready` en su clon antes de pintar, así que si acá se mide con la fallback
      // el pintado sale corrido respecto de la medición.
      await this.ensurePdfFonts();

      // Cabecera y pie se renderizan una sola vez y se estampan por página
      const chrome = await this.renderChrome(reportData);

      // Grupo A: contenido principal (resumen, indicadores, fortalezas, debilidades).
      // Se pagina automáticamente si desborda el alto de la hoja.
      const contentGroup = await this.renderHtml(this.crearContenidoPrincipal(reportData));
      const pagesA = this.addGroupToPdf(pdf, contentGroup, chrome, { isLastGroup: false, startOnNewPage: false });

      // Grupo B: tabla de recomendaciones — empieza en página nueva, salvo que el grupo A no
      // haya dibujado nada (si no, la primera hoja del documento quedaría vacía).
      const tableGroup = await this.renderHtml(this.crearContenidoRecomendaciones(reportData));
      this.addGroupToPdf(pdf, tableGroup, chrome, { isLastGroup: true, startOnNewPage: pagesA > 0 });

      // Open PDF
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Error al generar el PDF:', error);
      throw error;
    }
  }

  /**
   * Carga explícitamente las faces de Rubik que usa el reporte. `document.fonts.ready` no
   * alcanza por sí solo: espera las cargas en vuelo, y una face que ningún elemento pidió
   * todavía no está pendiente, así que resolvería sin haberla cargado.
   */
  private async ensurePdfFonts(): Promise<void> {
    try {
      await Promise.all([
        document.fonts.load('400 16px Rubik'), // párrafos y celdas
        document.fonts.load('500 16px Rubik'), // títulos de sección
        document.fonts.load('700 20px Rubik'), // h1 de la cabecera y el enlace myHotel
        document.fonts.load('italic 400 12px Rubik'), // el aviso
      ]);
      await document.fonts.ready;
    } catch (error) {
      // Si la carga falla se sigue adelante: el PDF saldrá con la fallback, pero medición y
      // pintado usarán la misma porque la geometría se toma del clon.
      console.warn('No se pudieron precargar las fuentes del PDF:', error);
    }
  }

  /**
   * Renderiza cabecera y pie a imágenes reutilizables que se estampan en cada página.
   * La cabecera se repite en todas las páginas; el pie solo en la última del documento.
   */
  private async renderChrome(jsonData: SmartReport): Promise<PdfChrome> {
    const header = await this.renderHtml(`${this.pdfStyles}${this.generateHeader(jsonData)}`);
    const footer = await this.renderHtml(`
      ${this.pdfStyles}
      <div class="pdf-report footer">
        <h3>${this.getPdfText('footer_title')}</h3>
        <a href="${MYHOTEL_URL}" target="_blank" data-pdf-link="${MYHOTEL_URL}" style="color: white; text-decoration: none;">www.myhotel.cl</a>
      </div>
    `);

    return {
      headerImg: header.canvas.toDataURL('image/png'),
      headerHeightMm: this.toMm(header.contentHeightPx, header.elementWidthPx),
      headerLinks: this.toChromeLinks(header),
      footerImg: footer.canvas.toDataURL('image/png'),
      footerHeightMm: this.toMm(footer.contentHeightPx, footer.elementWidthPx),
      footerLinks: this.toChromeLinks(footer),
    };
  }

  /** px CSS -> mm, sabiendo que el fragmento se renderiza a 210mm de ancho. */
  private toMm(valuePx: number, elementWidthPx: number): number {
    return (valuePx * PAGE_WIDTH_MM) / elementWidthPx;
  }

  private toChromeLinks(rendered: RenderedHtml): ChromeLink[] {
    return rendered.links.map((link) => ({
      leftMm: this.toMm(link.left, rendered.elementWidthPx),
      topMm: this.toMm(link.top, rendered.elementWidthPx),
      widthMm: this.toMm(link.width, rendered.elementWidthPx),
      heightMm: this.toMm(link.height, rendered.elementWidthPx),
      url: link.url,
    }));
  }

  private addChromeLinks(pdf: jsPDF, links: ChromeLink[], offsetYMm: number): void {
    for (const link of links) {
      pdf.link(link.leftMm, offsetYMm + link.topMm, link.widthMm, link.heightMm, { url: link.url });
    }
  }

  /**
   * Dibuja el grupo: pide los cortes a `planPages` (que trabaja en px CSS) y por cada franja
   * recorta el canvas, estampa la cabecera arriba, el contenido debajo y reposiciona los links.
   * El pie va solo en la franja que `planPages` marca con `withFooter`.
   *
   * Devuelve la cantidad de páginas dibujadas, para que el llamador sepa si hace falta abrir
   * página nueva para el grupo siguiente.
   */
  private addGroupToPdf(
    pdf: jsPDF,
    group: RenderedHtml,
    chrome: PdfChrome,
    opts: { isLastGroup: boolean; startOnNewPage: boolean },
  ): number {
    const { canvas, blockTopsPx, keepBoxesPx, links, elementWidthPx, contentHeightPx } = group;
    const { headerImg, headerHeightMm, footerImg, footerHeightMm } = chrome;

    // Escala vertical real del canvas respecto del contenido en px CSS. Se usa solo para
    // recortar: la geometría de página y los links se calculan en px CSS -> mm, sin pasar
    // por el ancho del canvas (que viene redondeado y desfasaba los cortes).
    // html2canvas construye el canvas como ceil(ceil(alto) * scale), así que el divisor tiene
    // que ser el alto redondeado hacia arriba para que la escala sea la que realmente usó.
    const scaleY = canvas.height / Math.ceil(contentHeightPx);

    const slices = planPages({
      contentHeightPx,
      breakPointsPx: this.collectBreakPoints(canvas, scaleY, blockTopsPx, keepBoxesPx),
      elementWidthPx,
      headerHeightMm,
      footerHeightMm,
      isLastGroup: opts.isLastGroup,
    });

    let drawnPages = 0;

    slices.forEach((slice) => {
      const sliceImgData = this.cropSlice(canvas, slice.offsetPx * scaleY, slice.heightPx * scaleY);
      if (!sliceImgData) return;

      // La página se abre recién cuando hay una franja que dibujar, para no dejar hojas vacías.
      if (drawnPages > 0 || opts.startOnNewPage) pdf.addPage();
      drawnPages++;

      // Cabecera en todas las páginas.
      pdf.addImage(headerImg, 'PNG', 0, 0, PAGE_WIDTH_MM, headerHeightMm, '', 'FAST');
      this.addChromeLinks(pdf, chrome.headerLinks, 0);

      // Contenido debajo de la cabecera.
      const sliceHeightMm = this.toMm(slice.heightPx, elementWidthPx);
      pdf.addImage(sliceImgData, 'PNG', 0, headerHeightMm, PAGE_WIDTH_MM, sliceHeightMm, '', 'FAST');

      // Links del contenido que caen dentro de esta franja.
      for (const link of links) {
        if (!link.url) continue;
        const linkBottomPx = link.top + link.height;
        if (linkBottomPx <= slice.offsetPx || link.top >= slice.offsetPx + slice.heightPx) continue;

        pdf.link(
          this.toMm(link.left, elementWidthPx),
          headerHeightMm + this.toMm(link.top - slice.offsetPx, elementWidthPx),
          this.toMm(link.width, elementWidthPx),
          this.toMm(link.height, elementWidthPx),
          { url: link.url },
        );
      }

      if (slice.withFooter) {
        const footerYMm = PAGE_HEIGHT_MM - footerHeightMm;
        pdf.addImage(footerImg, 'PNG', 0, footerYMm, PAGE_WIDTH_MM, footerHeightMm, '', 'FAST');
        this.addChromeLinks(pdf, chrome.footerLinks, footerYMm);
      }
    });

    return drawnPages;
  }

  /**
   * Cortes de página posibles del grupo, en px CSS.
   *
   * Los bordes finos salen del canvas ya pintado y no del DOM: html2canvas no deja que el
   * navegador pinte el texto, lo redibuja calculando él mismo la línea base, y los glifos caen
   * unos 10px por debajo de donde el DOM dice que está el renglón. Medido en el reporte: la banda
   * de un título aparece pintada exactamente en su caja del DOM (48-88px), pero un renglón cuyo
   * rect termina en 1007px tiene la tinta entre 1000 y 1015px. Cortar en 1007 partía las letras
   * al medio, que es el texto cortado entre páginas. Las filas sin tinta del canvas, en cambio,
   * están en el mismo espacio de coordenadas que se recorta, así que son seguras por construcción.
   *
   * Además, exigir la fila entera libre resuelve gratis la tabla a dos columnas: si a esa altura
   * hay texto en cualquiera de las dos celdas, la fila no es candidata.
   */
  private collectBreakPoints(
    canvas: HTMLCanvasElement,
    scaleY: number,
    blockTopsPx: number[],
    keepBoxesPx: VerticalSpan[],
  ): number[] {
    const { blankBandsPx, inkBandsPx } = this.scanInk(canvas, scaleY);
    const keepZones = this.extendKeepZones(keepBoxesPx, inkBandsPx);
    const crossesKeepZone = (yPx: number) =>
      keepZones.some((zone) => yPx > zone.topPx + GEOMETRY_EPSILON_PX && yPx < zone.bottomPx - GEOMETRY_EPSILON_PX);

    // El centro de cada hueco, no cualquier fila libre: así el blanco entre dos renglones se reparte
    // entre las dos páginas, en vez de que la siguiente empiece con el texto pegado a la cabecera.
    const gapCentersPx = blankBandsPx.map((band) => (band.topPx + band.bottomPx) / 2);

    return [...blockTopsPx, ...gapCentersPx].filter((yPx) => !crossesKeepZone(yPx)).sort((a, b) => a - b);
  }

  /**
   * Recorre el canvas por franjas de filas y devuelve las bandas libres y las bandas con tinta, en
   * px CSS. Una fila cuenta como libre si su tinta no pasa de `MAX_BLANK_ROW_INK_RATIO`: así los
   * bordes verticales de la tabla no impiden cortar entre los renglones de una celda, pero un fondo
   * de color, que ocupa el ancho completo, sigue siendo intocable.
   */
  private scanInk(
    canvas: HTMLCanvasElement,
    scaleY: number,
  ): { blankBandsPx: VerticalSpan[]; inkBandsPx: VerticalSpan[] } {
    // El canvas ya trae su contexto creado por html2canvas, así que acá solo se recupera. Se lee por
    // franjas para no levantar el canvas completo (decenas de MB) de una sola vez.
    const ctx = canvas.getContext('2d');
    if (!ctx) return { blankBandsPx: [], inkBandsPx: [] };

    const blankBandsPx: VerticalSpan[] = [];
    const inkBandsPx: VerticalSpan[] = [];
    const maxBlankInk = canvas.width * MAX_BLANK_ROW_INK_RATIO;
    let openInkRow: number | null = null;
    let openBlankRow = 0;

    for (let stripTop = 0; stripTop < canvas.height; stripTop += INK_SCAN_STRIP_ROWS) {
      const stripRows = Math.min(INK_SCAN_STRIP_ROWS, canvas.height - stripTop);
      const { data } = ctx.getImageData(0, stripTop, canvas.width, stripRows);

      for (let row = 0; row < stripRows; row++) {
        const rowStart = row * canvas.width * 4;
        const rowEnd = rowStart + canvas.width * 4;
        let ink = 0;
        for (let i = rowStart; i < rowEnd && ink <= maxBlankInk; i += 4) {
          if (data[i] < INK_THRESHOLD || data[i + 1] < INK_THRESHOLD || data[i + 2] < INK_THRESHOLD) ink++;
        }
        const inked = ink > maxBlankInk;

        const canvasRow = stripTop + row;
        if (inked && openInkRow === null) {
          openInkRow = canvasRow;
          blankBandsPx.push({ topPx: openBlankRow / scaleY, bottomPx: canvasRow / scaleY });
        } else if (!inked && openInkRow !== null) {
          openBlankRow = canvasRow;
          inkBandsPx.push({ topPx: openInkRow / scaleY, bottomPx: canvasRow / scaleY });
          openInkRow = null;
        }
      }
    }

    // La banda que quedó abierta llega hasta el final del canvas.
    const endPx = canvas.height / scaleY;
    if (openInkRow !== null) inkBandsPx.push({ topPx: openInkRow / scaleY, bottomPx: endPx });
    else blankBandsPx.push({ topPx: openBlankRow / scaleY, bottomPx: endPx });

    return { blankBandsPx, inkBandsPx };
  }

  /**
   * Extiende cada caja indivisible (`data-pdf-keep-next`: los títulos de sección y el encabezado de
   * la tabla) hasta el final de la primera banda con tinta que le sigue, o sea su primera línea de
   * contenido. Así ningún corte deja un título colgando solo al pie de una página.
   */
  private extendKeepZones(keepBoxesPx: VerticalSpan[], inkBandsPx: VerticalSpan[]): VerticalSpan[] {
    return keepBoxesPx.map((box) => {
      const nextBand = inkBandsPx.find((band) => band.topPx >= box.bottomPx - GEOMETRY_EPSILON_PX);
      return { topPx: box.topPx, bottomPx: nextBand ? nextBand.bottomPx : box.bottomPx };
    });
  }

  /**
   * Recorta una franja del canvas del grupo. Los límites se redondean a píxel entero para que
   * las franjas queden contiguas, sin perder ni duplicar una fila de píxeles entre páginas.
   */
  private cropSlice(canvas: HTMLCanvasElement, startPx: number, heightPx: number): string | null {
    const start = Math.max(0, Math.round(startPx));
    const end = Math.min(canvas.height, Math.round(startPx + heightPx));
    const height = end - start;
    if (height < 1) return null;

    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = height;

    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) return null;

    ctx.drawImage(canvas, 0, start, canvas.width, height, 0, 0, canvas.width, height);
    return sliceCanvas.toDataURL('image/png');
  }

  /**
   * Renderiza un fragmento HTML off-screen a un único canvas.
   *
   * La geometría (alto, cortes de página y links) se mide DENTRO del clon que arma html2canvas,
   * no en el documento vivo: html2canvas no rasteriza el DOM real, lo clona en un iframe y lo
   * vuelve a maquetar, y solo llama a `onclone` cuando ese clon ya está maquetado y con las
   * fuentes cargadas (espera `documentClone.fonts.ready`), justo antes de pintar. Midiendo ahí,
   * medición y pintado comparten un único layout: si se mide en el documento vivo y la fuente
   * todavía no estaba aplicada, cada corte cae desplazado y el texto sale cortado o encimado.
   *
   * El viewport de ese clon se fija al tamaño del fragmento, así el resultado no depende del
   * tamaño de la ventana del usuario.
   */
  private async renderHtml(html: string): Promise<RenderedHtml> {
    const tempDiv = document.createElement('div');
    tempDiv.style.width = '210mm';
    tempDiv.style.margin = '0';
    tempDiv.style.position = 'absolute';
    tempDiv.style.top = '0';
    tempDiv.style.left = '-9999px';
    tempDiv.style.background = '#ffffff';
    tempDiv.innerHTML = html;
    document.body.appendChild(tempDiv);

    try {
      // Solo para dimensionar el viewport del clon; la geometría sale de `measureFragment`.
      const liveRect = tempDiv.getBoundingClientRect();
      const size = { width: Math.ceil(liveRect.width), height: Math.ceil(liveRect.height) };

      let measured: FragmentMeasures | null = null;
      const canvas = await html2canvas(tempDiv, {
        windowWidth: size.width,
        windowHeight: size.height,
        scrollX: 0,
        scrollY: 0,
        backgroundColor: '#ffffff',
        // Antes del onclone a propósito: deja sobrescribir escala y viewport desde fuera para
        // probar el reporte con distintas resoluciones simuladas.
        ...this.pdfOptions,
        onclone: (_documentClone, clonedRoot) => {
          measured = this.measureFragment(clonedRoot as HTMLElement);
        },
      });

      if (!measured) throw new Error('No se pudo medir el fragmento del PDF en el clon');

      if (Math.abs(measured.contentHeightPx - liveRect.height) > 4) {
        console.warn('El layout del clon no coincide con el del documento vivo', {
          clon: measured.contentHeightPx,
          vivo: liveRect.height,
        });
      }

      return { canvas, ...measured };
    } finally {
      document.body.removeChild(tempDiv);
    }
  }

  /**
   * Mide el fragmento ya maquetado: alto, ancho, los bloques donde conviene cortar página
   * (`data-pdf-block`), las cajas que no se pueden partir (`data-pdf-keep-next`) y los links
   * (`data-pdf-link`), todo relativo a la raíz y en px CSS.
   *
   * Acá solo se miden CAJAS, nunca texto: html2canvas pinta los fondos y bordes exactamente donde
   * el DOM dice, pero redibuja los glifos con su propia línea base y los baja ~10px, así que un
   * borde de renglón medido en el DOM no corresponde a la tinta del canvas. Los cortes finos entre
   * renglones los aporta `collectBreakPoints` leyendo el canvas.
   */
  private measureFragment(root: HTMLElement): FragmentMeasures {
    const rootRect = root.getBoundingClientRect();

    const blockTopsPx: number[] = [];
    root.querySelectorAll('[data-pdf-block]').forEach((el) => {
      blockTopsPx.push((el as HTMLElement).getBoundingClientRect().top - rootRect.top);
    });

    const keepBoxesPx: VerticalSpan[] = [];
    root.querySelectorAll('[data-pdf-keep-next]').forEach((el) => {
      const rect = (el as HTMLElement).getBoundingClientRect();
      keepBoxesPx.push({ topPx: rect.top - rootRect.top, bottomPx: rect.bottom - rootRect.top });
    });

    const links: PdfLink[] = [];
    root.querySelectorAll('[data-pdf-link]').forEach((el) => {
      const rect = (el as HTMLElement).getBoundingClientRect();
      links.push({
        top: rect.top - rootRect.top,
        left: rect.left - rootRect.left,
        width: rect.width,
        height: rect.height,
        url: (el as HTMLElement).getAttribute('data-pdf-link') || '',
      });
    });

    return {
      blockTopsPx,
      keepBoxesPx,
      links,
      elementWidthPx: rootRect.width,
      contentHeightPx: rootRect.height,
    };
  }

  /**
   * Contenido principal SIN cabecera ni pie (esos se estampan por página en addGroupToPdf).
   * Cada sección se marca con `data-pdf-block` para que los saltos de página caigan de preferencia
   * entre secciones, y cada título con `data-pdf-keep-next` para que no quede colgando al pie de
   * una página cuando la sección es más alta que la hoja y hay que cortarla por dentro.
   */
  private crearContenidoPrincipal(jsonData: SmartReport): string {
    const aviso = `
      <div data-pdf-block class="aviso">
        <p>${this.getPdfText('ai_notice')}</p>
      </div>
    `;

    const resumenEjecutivo = `
      <div data-pdf-block class="seccion">
        <div data-pdf-keep-next class="seccion-titulo">${this.getPdfText('executive_summary')}</div>
        <div class="seccion-contenido">
          <p>${this.modelText(jsonData.general_overview)}</p>
        </div>
      </div>
    `;

    const indicators = [
      {
        title: this.getPdfText('onsite_nps'),
        description: jsonData.onsite_nps,
      },
      {
        title: this.getPdfText('followup_nps'),
        description: jsonData.followup_nps,
      },
      {
        title: this.getPdfText('online_reputation'),
        description: jsonData.iro,
      },
    ];
    const variacionIndicadores = `
      <div data-pdf-block class="seccion">
        <div data-pdf-keep-next class="seccion-titulo">${this.getPdfText('indicator_variation')}</div>
        <div class="seccion-contenido">
          ${this.generateIndicators(indicators)}
        </div>
      </div>
    `;

    const fortalezas = `
      <div data-pdf-block class="seccion">
        <div data-pdf-keep-next class="seccion-titulo">${this.getPdfText('strengths')}</div>
        <div class="seccion-contenido">
          <p>${this.modelText(jsonData.general_strengths)}</p>
        </div>
      </div>
    `;

    const debilidades = `
      <div data-pdf-block class="seccion">
        <div data-pdf-keep-next class="seccion-titulo">${this.getPdfText('weaknesses')}</div>
        <div class="seccion-contenido">
          <p>${this.modelText(jsonData.general_weakness)}</p>
        </div>
      </div>
    `;

    return `
      ${this.pdfStyles}
      <div class="pdf-report contenido">
        ${aviso}
        ${resumenEjecutivo}
        ${variacionIndicadores}
        ${fortalezas}
        ${debilidades}
      </div>
    `;
  }

  /** Tabla de recomendaciones SIN cabecera ni pie; empieza en página nueva desde createPdfReport. */
  private crearContenidoRecomendaciones(jsonData: SmartReport): string {
    const tablaRecomendaciones = this.generateRecommendationsTable(jsonData.recommendations || []);

    return `
      ${this.pdfStyles}
      <div class="pdf-report contenido">
        ${tablaRecomendaciones}
      </div>
    `;
  }

  private generateHeader(jsonData: SmartReport) {
    return `
      <div class="pdf-report header">
        <div>
          <h1>${jsonData.customer_name}</h1>
          <h2>${this.getPdfText('report_title')} - ${jsonData.report_month_str} ${jsonData.report_year}</h2>
        </div>
        <div style="padding-bottom: 20px;">
          <p class="prepared-text">${this.getPdfText('prepared_by')}</p>
          <a href="${MYHOTEL_URL}" target="_blank" data-pdf-link="${MYHOTEL_URL}" style="color: white; text-decoration: none; font-weight: bold; font-size: 25px; padding: 0; line-height: 1;">myHotel</a>
        </div>
      </div>
    `;
  }

  private generateIndicators(indicators: any[]): string {
    let htmlIndicators = '';
    const noData = this.translateService.instant(
      'interchain-dashboard.qualification-texts.no-data-description-individual',
    );
    indicators.forEach((indicator) => {
      htmlIndicators += `
        <p><strong>${indicator.title}:</strong> ${this.modelText(indicator.description) || noData}</p>
      `;
    });

    return htmlIndicators;
  }

  private generateRecommendationsTable(recomendations: any[]): string {
    let tablaHTML = `
      <div class="seccion">
        <div data-pdf-block data-pdf-keep-next class="seccion-titulo">${this.getPdfText('recommendations')}</div>
        <div class="seccion-contenido">
          <table class="tabla-recomendaciones">
            <thead>
              <tr data-pdf-block data-pdf-keep-next>
                <th>${this.getPdfText('recommendation')}</th>
                <th>${this.getPdfText('reason')}</th>
              </tr>
            </thead>
            <tbody>
    `;

    recomendations.forEach((recommendation) => {
      tablaHTML += `
        <tr data-pdf-block>
          <td>${this.modelText(recommendation.recommendation)}</td>
          <td><p>${this.modelText(recommendation.reason)}</p></td>
        </tr>
      `;
    });

    tablaHTML += `
            </tbody>
          </table>
        </div>
      </div>
    `;

    return tablaHTML;
  }

  private getPdfText(key: string): string {
    return this.translateService.instant(`interchain-dashboard.smart_report_pdf.${key}`);
  }

  /**
   * Texto generado por el modelo, listo para incrustar. Los `\n` internos los respeta el CSS
   * (`white-space: pre-line`), pero un salto al principio o al final abriría una línea vacía
   * dentro del párrafo, así que se recortan los extremos.
   */
  private modelText(value: string): string {
    return (value || '').trim();
  }

  get pastMonth() {
    const currentDate = new Date();
    const pastMonth = currentDate.getMonth() === 0 ? 11 : currentDate.getMonth() - 1;
    return pastMonth;
  }

  get pastYear() {
    const currentDate = new Date();
    const pastYear = currentDate.getMonth() === 0 ? currentDate.getFullYear() - 1 : currentDate.getFullYear();
    return pastYear;
  }

  get isMobile() {
    return this.breakpointService.isMobile();
  }
}
